import {
  isRedactableFragmentParam,
  isRedactableQueryParam,
  pathCredentialPrefixFor,
} from "./bearer-urls";

const REDACTED = "redacted";

export function redactPathCredential(pathname: string): string {
  const credential = pathCredentialPrefixFor(pathname);
  if (credential === null) return pathname;

  const { prefix, credentialSegments } = credential;
  const remainder = pathname.slice(prefix.length).replace(/^\//, "");
  if (remainder.length === 0) return pathname;

  const segments = remainder.split("/");
  const redactedSegments = segments.map((segment, index) =>
    index < credentialSegments ? REDACTED : segment,
  );
  const base = prefix.endsWith("/") ? prefix : `${prefix}/`;
  return `${base}${redactedSegments.join("/")}`;
}

function redactParams(
  params: URLSearchParams,
  pathname: string,
  isRedactable: (name: string, pathname: string) => boolean,
): boolean {
  let changed = false;
  for (const name of [...params.keys()]) {
    if (!isRedactable(name, pathname)) continue;
    params.set(name, REDACTED);
    changed = true;
  }
  return changed;
}

export function redactUrlCredentials(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return rawUrl;
  }

  let changed = false;

  const redactedPath = redactPathCredential(url.pathname);
  if (redactedPath !== url.pathname) {
    url.pathname = redactedPath;
    changed = true;
  }

  if (redactParams(url.searchParams, url.pathname, isRedactableQueryParam)) changed = true;

  if (url.hash.length > 1) {
    const fragment = new URLSearchParams(url.hash.slice(1));
    if (redactParams(fragment, url.pathname, isRedactableFragmentParam)) {
      url.hash = `#${fragment.toString()}`;
      changed = true;
    }
  }

  return changed ? url.toString() : rawUrl;
}

const RELATIVE_URL_BASE = "https://relative.invalid";

const EMBEDDED_URL_PATTERN =
  /((?:https?:)?\/\/[^\s"'<>`()[\]]+)|(^|[^\w/.:%-])(\/(?!\/)[^\s"'<>`()[\]]*)|(^|[^\w/])([?#][^\s"'<>`()[\]]+)/g;

function redactNestedParamValues(rawUrl: string): string {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return rawUrl;
  }
  let changed = false;
  const entries = [...url.searchParams].map(([name, value]) => {
    const redacted = redactCredentialsInText(value);
    if (redacted !== value) changed = true;
    return [name, redacted];
  });
  if (!changed) return rawUrl;
  url.search = new URLSearchParams(entries).toString();
  return url.toString();
}

function redactAbsoluteUrl(rawUrl: string): string {
  return redactNestedParamValues(redactUrlCredentials(rawUrl));
}

function redactProtocolRelativeUrl(match: string): string {
  if (!match.startsWith("//")) return redactAbsoluteUrl(match);
  const redacted = redactAbsoluteUrl(`https:${match}`);
  return redacted === `https:${match}` ? match : redacted.slice("https:".length);
}

function redactRelativeUrl(match: string, prefix: string): string {
  const absolute = `${RELATIVE_URL_BASE}${prefix}${match}`;
  const redacted = redactAbsoluteUrl(absolute);
  if (redacted === absolute) return match;
  return redacted.slice(RELATIVE_URL_BASE.length + prefix.length);
}

export function redactCredentialsInText(text: string): string {
  return text.replace(
    EMBEDDED_URL_PATTERN,
    (
      match: string,
      absolute: string | undefined,
      pathLead: string | undefined,
      path: string | undefined,
      queryLead: string | undefined,
      query: string | undefined,
    ) => {
      if (absolute !== undefined) return redactProtocolRelativeUrl(absolute);
      if (path !== undefined) return `${pathLead ?? ""}${redactRelativeUrl(path, "")}`;
      if (query !== undefined) return `${queryLead ?? ""}${redactRelativeUrl(query, "/")}`;
      return match;
    },
  );
}
